import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormMoneyField from '../../../FormFields/FormMoneyField';
import FormNumberField from '../../../FormFields/FormNumberField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { formCardStyle } from '../../../TransitionStyles';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { TELECOMMUNICATIONS } from '../../../formQuestionsEnum';

TVForm.propTypes = {
  permissions: PropTypes.object,
};

export default function TVForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const fieldIds = [
    TELECOMMUNICATIONS.TV_PROVIDER.id,
    TELECOMMUNICATIONS.TV_MONTHLY_COST.id,
    TELECOMMUNICATIONS.IS_TV_EQUIPMENT_LEASED.id,
    TELECOMMUNICATIONS.TV_LEASE_MONTHLY_COST.id,
    TELECOMMUNICATIONS.CHANNEL_LINEUP.id,
    TELECOMMUNICATIONS.NUMBER_OF_TV_OUTLETS.id,
    TELECOMMUNICATIONS.NUMBER_OF_TV_CHANNELS.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  const { watch } = useFormContext();
  const equipmentLeased = watch(TELECOMMUNICATIONS.IS_TV_EQUIPMENT_LEASED.fieldName);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          TV
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {TELECOMMUNICATIONS.TV_PROVIDER.id in permissions && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'TV provider', fieldType: 'title' }} />
                <FormVendorField
                  fieldName={TELECOMMUNICATIONS.TV_PROVIDER.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.TV_PROVIDER.id] }}
                  invoiceFileTypeId={FILE_TYPES.TV_AND_CABLE_PROVIDER_INVOICE}
                  contractFileTypeId={FILE_TYPES.TV_AND_CABLE_PROVIDER_CONTRACT}
                />
              </FieldContainer>
            </Grid>
          )}
          {TELECOMMUNICATIONS.TV_MONTHLY_COST.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Current monthly cost' }} />
              <FormMoneyField
                fieldName={TELECOMMUNICATIONS.TV_MONTHLY_COST.fieldName}
                fieldAttributes={{
                  isEditable: permissions[TELECOMMUNICATIONS.TV_MONTHLY_COST.id],
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {TELECOMMUNICATIONS.IS_TV_EQUIPMENT_LEASED.id in permissions && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Equipment' }} />
                <FormButtonOptions
                  fieldName={TELECOMMUNICATIONS.IS_TV_EQUIPMENT_LEASED.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[TELECOMMUNICATIONS.IS_TV_EQUIPMENT_LEASED.id],
                    options: [
                      { label: 'Owned', value: false },
                      { label: 'Leased', value: true },
                    ],
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {equipmentLeased && TELECOMMUNICATIONS.TV_LEASE_MONTHLY_COST.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Monthly cost to lease' }} />
              <FormMoneyField
                fieldName={TELECOMMUNICATIONS.TV_LEASE_MONTHLY_COST.fieldName}
                fieldAttributes={{
                  isEditable: permissions[TELECOMMUNICATIONS.TV_LEASE_MONTHLY_COST.id],
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {TELECOMMUNICATIONS.CHANNEL_LINEUP.id in permissions && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'Current channel lineup', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={TELECOMMUNICATIONS.CHANNEL_LINEUP.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[TELECOMMUNICATIONS.CHANNEL_LINEUP.id],
                    fileTypeId: FILE_TYPES.CHANNEL_LINEUP,
                  }}
                />
              </Box>
            </Grid>
          )}
          {TELECOMMUNICATIONS.NUMBER_OF_TV_OUTLETS.id in permissions && (
            <Grid item xs={12} lg={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Number of TV outlets in the facility' }} />
                <FormNumberField
                  fieldName={TELECOMMUNICATIONS.NUMBER_OF_TV_OUTLETS.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.NUMBER_OF_TV_OUTLETS.id] }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}
          {TELECOMMUNICATIONS.NUMBER_OF_TV_CHANNELS.id in permissions && (
            <Grid item xs={12} lg={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Number of channels' }} />
                <FormNumberField
                  fieldName={TELECOMMUNICATIONS.NUMBER_OF_TV_CHANNELS.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.NUMBER_OF_TV_CHANNELS.id] }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
